import type {
  RequiredUiBlockFamilyLiveSwapReceipt,
  RequiredUiBlockFamilyLiveSwapRelease,
} from "@cogo/schema/required-ui-block-admission"

import { stableSerialize } from "./design-ontology-live-swap"

type Sha256 = `sha256:${string}`

export interface RequiredUiBlockFamilyEvidenceSourceRelease
  extends RequiredUiBlockFamilyLiveSwapRelease {
  ref: string
}

export interface RequiredUiBlockFamilyEvidenceSourceArtifact {
  artifactRef: string
  artifactHash: Sha256
  bundleFiles: RequiredUiBlockFamilyBundleFile[]
  baselineSource: {
    kind: "migrated-source-snapshot" | "authored-baseline-role-map"
    visualInputs: unknown[]
    snapshotContentHash: Sha256
    terminalReferenceRef: string
  }
}

export interface RequiredUiBlockFamilyBundleFile {
  kind: "block" | "datasource" | "events" | "flow" | "ui" | "variables"
  ref: string
  contentHash: Sha256
}

export interface RequiredUiBlockFamilyVisualProbeSource {
  dimension: "typography" | "radius" | "density" | "color" | "responsive"
  artifactRef: string
  elementId: string
  property: string
  properties?: string[]
  role: string
  expectedDistinctAcrossReleases?: boolean
  releaseRoleValues: Record<
    RequiredUiBlockFamilyLiveSwapRelease["releaseId"],
    string
  >
}

export interface RequiredUiBlockFamilyEvidenceSource {
  patternId: string
  familyId: string
  variantId: string
  familyNamespace: string
  canonicalArtifacts: RequiredUiBlockFamilyEvidenceSourceArtifact[]
  familyBlockArtifactHash: Sha256
  detailedRoles: string[]
  sourceContracts: {
    eventIdentities: string[]
    focusTargets: Array<{ artifactRef: string; elementId: string }>
    interactionStatePaths: Array<{ artifactRef: string; path: string }>
    visualProbes: RequiredUiBlockFamilyVisualProbeSource[]
  }
  terminalReceiptRef: string
  terminalBuilderRefreshRequired: string[]
}

export interface RequiredUiBlockFamilyEvidenceSourcesV1 {
  schemaVersion: "cogo.required-ui-block-family-evidence-sources.v1"
  sourceId: string
  phase: "source-definition-only"
  styleScopes: ["luma", "lyra", "mira", "nova", "vega", "maia"]
  rendererContract: {
    package: "@cogo/react-renderer"
    entrypoint: string
    renderer: "ElementRenderer"
    designResolutionOrder: "expression-then-design-handle"
    mountedBrowserReceiptKind: "real-cogo-react-renderer-mounted-headless-chromium"
  }
  releaseSet: RequiredUiBlockFamilyEvidenceSourceRelease[]
  families: RequiredUiBlockFamilyEvidenceSource[]
  contentHash: Sha256
}

export interface RequiredUiBlockFamilyReleaseObservation {
  releaseId: RequiredUiBlockFamilyLiveSwapRelease["releaseId"]
  releaseContentHash: Sha256
  releaseEpoch: number
  mountId: string
  rendererNodeIdentity: string
  blockArtifactHash: Sha256
  structuralFingerprint: string
  interactionState: unknown
  focusedElementId: string | null
  focusContractuallyRestored: boolean
  actionFlowTrace: unknown
  visualProbes: Record<string, string>
  resolvedDesignRoles: Record<string, string>
  unresolvedDesignAuthorityCount: number
  unknownElementCount: number
  consoleErrorCount: number
}

export interface RequiredUiBlockFamilyArtifactObservation {
  artifactRef: string
  bundleFiles: RequiredUiBlockFamilyBundleFile[]
  baselineSourceSnapshotContentHash: Sha256
  baselineDifferentPixels: number
  baselineTotalPixels: number
  releases: RequiredUiBlockFamilyReleaseObservation[]
}

export interface RequiredUiBlockFamilyBrowserObservation {
  patternId: string
  familyId: string
  variantId: string
  evidenceSourceContentHash: Sha256
  rendererContract: RequiredUiBlockFamilyEvidenceSourcesV1["rendererContract"]
  artifacts: RequiredUiBlockFamilyArtifactObservation[]
}

const RELEASE_IDS: RequiredUiBlockFamilyLiveSwapRelease["releaseId"][] = [
  "baseline.v1",
  "editorial.v1",
  "compact.v1",
]

/**
 * Selects one canonical family source. It does not admit the family: the
 * returned definition is only the immutable input for a later mounted-browser
 * observation and receipt build.
 */
export function requiredUiBlockFamilyEvidenceSource(
  sources: RequiredUiBlockFamilyEvidenceSourcesV1,
  patternId: string,
): RequiredUiBlockFamilyEvidenceSource {
  const family = sources.families.find((candidate) => candidate.patternId === patternId)
  if (!family) throw new Error(`required-ui-block-family-source-missing:${patternId}`)
  return family
}

/**
 * Reduces measurements from the two real family artifacts into Task 5's exact
 * G3 receipt shape. Every pass fact is derived from snapshots; callers cannot
 * inject pre-computed admission booleans.
 */
export function buildRequiredUiBlockFamilyLiveSwapReceipt(
  sources: RequiredUiBlockFamilyEvidenceSourcesV1,
  family: RequiredUiBlockFamilyEvidenceSource,
  observation: RequiredUiBlockFamilyBrowserObservation,
): RequiredUiBlockFamilyLiveSwapReceipt {
  const blockers: string[] = []
  const block = (value: string): void => {
    if (!blockers.includes(value)) blockers.push(value)
  }

  if (
    observation.patternId !== family.patternId ||
    observation.familyId !== family.familyId ||
    observation.variantId !== family.variantId
  ) {
    block("family-identity-mismatch")
  }
  const canonicalFamily = sources.families.find(
    (candidate) => candidate.patternId === family.patternId,
  )
  const familySourceBindingPass = canonicalFamily !== undefined &&
    stableSerialize(canonicalFamily) === stableSerialize(family)
  if (!familySourceBindingPass) block("family-source-binding-mismatch")
  const evidenceSourceContentHashPass =
    /^sha256:[a-f0-9]{64}$/.test(sources.contentHash) &&
    observation.evidenceSourceContentHash === sources.contentHash
  if (!evidenceSourceContentHashPass) block("evidence-source-content-hash-mismatch")
  if (stableSerialize(observation.rendererContract) !== stableSerialize(sources.rendererContract)) {
    block("renderer-contract-mismatch")
  }

  const canonicalArtifactRefs = family.canonicalArtifacts.map((artifact) => artifact.artifactRef)
  const observedArtifactRefs = observation.artifacts.map((artifact) => artifact.artifactRef)
  const canonicalArtifactSet =
    canonicalArtifactRefs.length === 2 &&
    new Set(canonicalArtifactRefs).size === 2 &&
    stableSerialize(observedArtifactRefs) === stableSerialize(canonicalArtifactRefs)
  if (!canonicalArtifactSet) block("canonical-two-artifact-set-mismatch")

  const releaseSet = releaseReceiptSet(sources.releaseSet, block)
  let baselineSourceAdmissionPass = canonicalArtifactSet &&
    familySourceBindingPass &&
    evidenceSourceContentHashPass
  let ontologyReleaseSwapPass = canonicalArtifactSet
  let blockArtifactHashUnchanged = canonicalArtifactSet
  let structuralFingerprintUnchanged = canonicalArtifactSet
  let interactionStatePreserved = canonicalArtifactSet
  let focusPreservedOrContractuallyRestored = canonicalArtifactSet
  let actionFlowTraceUnchanged = canonicalArtifactSet
  let unresolvedDesignAuthorityCount = 0
  let unknownElementCount = 0
  let consoleErrorCount = 0

  for (let index = 0; index < family.canonicalArtifacts.length; index += 1) {
    const sourceArtifact = family.canonicalArtifacts[index]
    const measuredArtifact = observation.artifacts[index]
    if (!measuredArtifact || measuredArtifact.artifactRef !== sourceArtifact.artifactRef) {
      baselineSourceAdmissionPass = false
      ontologyReleaseSwapPass = false
      blockArtifactHashUnchanged = false
      structuralFingerprintUnchanged = false
      interactionStatePreserved = false
      focusPreservedOrContractuallyRestored = false
      actionFlowTraceUnchanged = false
      continue
    }

    const bundleFileContentAddressesMatch =
      stableSerialize(measuredArtifact.bundleFiles) === stableSerialize(sourceArtifact.bundleFiles)
    if (!bundleFileContentAddressesMatch) {
      block(`bundle-file-content-address-mismatch:${sourceArtifact.artifactRef}`)
    }
    baselineSourceAdmissionPass &&= bundleFileContentAddressesMatch

    const snapshots = measuredArtifact.releases
    const snapshotReleaseIds = snapshots.map((snapshot) => snapshot.releaseId)
    const releaseOrderComplete =
      stableSerialize(snapshotReleaseIds) === stableSerialize(RELEASE_IDS)
    const releaseHashesMatch = releaseOrderComplete && snapshots.every(
      (snapshot, releaseIndex) =>
        snapshot.releaseContentHash === releaseSet[releaseIndex]?.contentHash,
    )
    const releaseEpochsAdvance = releaseOrderComplete && snapshots.every(
      (snapshot, releaseIndex) =>
        Number.isInteger(snapshot.releaseEpoch) &&
        (releaseIndex === 0 || snapshot.releaseEpoch > snapshots[releaseIndex - 1].releaseEpoch),
    )
    const mountStable = stableNonEmpty(snapshots.map((snapshot) => snapshot.mountId))
    const nodeIdentityStable = stableNonEmpty(
      snapshots.map((snapshot) => snapshot.rendererNodeIdentity),
    )
    if (!releaseOrderComplete) block(`release-order-incomplete:${sourceArtifact.artifactRef}`)
    if (!releaseHashesMatch) block(`release-content-hash-mismatch:${sourceArtifact.artifactRef}`)
    if (!releaseEpochsAdvance) block(`release-epoch-not-advanced:${sourceArtifact.artifactRef}`)
    if (!mountStable || !nodeIdentityStable) block(`renderer-remounted:${sourceArtifact.artifactRef}`)
    ontologyReleaseSwapPass &&=
      releaseOrderComplete && releaseHashesMatch && releaseEpochsAdvance && mountStable && nodeIdentityStable

    const baselineReferenceMatches =
      measuredArtifact.baselineSourceSnapshotContentHash ===
      sourceArtifact.baselineSource.snapshotContentHash
    const exactBaselinePixels =
      Number.isInteger(measuredArtifact.baselineDifferentPixels) &&
      measuredArtifact.baselineDifferentPixels === 0 &&
      Number.isInteger(measuredArtifact.baselineTotalPixels) &&
      measuredArtifact.baselineTotalPixels > 0
    if (!baselineReferenceMatches || !exactBaselinePixels) {
      block(`baseline-source-parity-mismatch:${sourceArtifact.artifactRef}`)
    }
    baselineSourceAdmissionPass &&= baselineReferenceMatches && exactBaselinePixels

    const artifactHashStable =
      snapshots.length === 3 &&
      snapshots.every((snapshot) => snapshot.blockArtifactHash === sourceArtifact.artifactHash)
    const fingerprintStable = stableNonEmpty(
      snapshots.map((snapshot) => snapshot.structuralFingerprint),
    )
    const stateStable = stableSerialized(snapshots.map((snapshot) => snapshot.interactionState))
    const focusedIds = snapshots.map((snapshot) => snapshot.focusedElementId)
    const focusStable =
      focusedIds[0] !== null && stableSerialized(focusedIds)
    const focusRestored = snapshots.length === 3 && snapshots.every(
      (snapshot) => snapshot.focusContractuallyRestored,
    )
    const traceStable = stableSerialized(snapshots.map((snapshot) => snapshot.actionFlowTrace))
    if (!artifactHashStable) block(`block-artifact-hash-changed:${sourceArtifact.artifactRef}`)
    if (!fingerprintStable) block(`structural-fingerprint-changed:${sourceArtifact.artifactRef}`)
    if (!stateStable) block(`interaction-state-reset:${sourceArtifact.artifactRef}`)
    if (!focusStable && !focusRestored) block(`focus-not-preserved:${sourceArtifact.artifactRef}`)
    if (!traceStable) block(`action-flow-trace-changed:${sourceArtifact.artifactRef}`)
    blockArtifactHashUnchanged &&= artifactHashStable
    structuralFingerprintUnchanged &&= fingerprintStable
    interactionStatePreserved &&= stateStable
    focusPreservedOrContractuallyRestored &&= focusStable || focusRestored
    actionFlowTraceUnchanged &&= traceStable

    unresolvedDesignAuthorityCount += Math.max(
      0,
      ...snapshots.map((snapshot) => nonNegativeInteger(snapshot.unresolvedDesignAuthorityCount)),
    )
    unknownElementCount += Math.max(
      0,
      ...snapshots.map((snapshot) => nonNegativeInteger(snapshot.unknownElementCount)),
    )
    consoleErrorCount += Math.max(
      0,
      ...snapshots.map((snapshot) => nonNegativeInteger(snapshot.consoleErrorCount)),
    )
  }

  const declaredProbeRoleValuesPass = family.sourceContracts.visualProbes.length === 4 &&
    family.sourceContracts.visualProbes.every((probe) => {
      const artifact = observation.artifacts.find(
        (candidate) => candidate.artifactRef === probe.artifactRef,
      )
      const declaredValues = RELEASE_IDS.map(
        (releaseId) => probe.releaseRoleValues[releaseId]?.trim() ?? "",
      )
      const resolvedValues = RELEASE_IDS.map((releaseId) => artifact?.releases.find(
        (snapshot) => snapshot.releaseId === releaseId,
      )?.resolvedDesignRoles[probe.role]?.trim() ?? "")
      return family.detailedRoles.includes(probe.role) &&
        declaredValues.every(Boolean) &&
        stableSerialize(resolvedValues) === stableSerialize(declaredValues)
    })
  if (!declaredProbeRoleValuesPass) block("declared-probe-role-value-mismatch")
  const computedProbeValuesPass = family.sourceContracts.visualProbes.length === 4 &&
    family.sourceContracts.visualProbes.every((probe) => {
      const artifact = observation.artifacts.find(
        (candidate) => candidate.artifactRef === probe.artifactRef,
      )
      const key = `${probe.elementId}:${probe.property}`
      const values = RELEASE_IDS.map((releaseId) => artifact?.releases.find(
        (snapshot) => snapshot.releaseId === releaseId,
      )?.visualProbes[key]?.trim() ?? "")
      return values.every(Boolean) && (
        probe.expectedDistinctAcrossReleases === false ||
        new Set(values).size > 1
      )
    })
  if (!computedProbeValuesPass) block("computed-probe-value-mismatch")
  const atLeastOneComputedProbeChanges = family.sourceContracts.visualProbes.some((probe) => {
    const artifact = observation.artifacts.find(
      (candidate) => candidate.artifactRef === probe.artifactRef,
    )
    const key = `${probe.elementId}:${probe.property}`
    return new Set(RELEASE_IDS.map((releaseId) => artifact?.releases.find(
      (snapshot) => snapshot.releaseId === releaseId,
    )?.visualProbes[key]?.trim() ?? "")).size > 1
  })
  const alternateReleaseProbeChangesPass =
    declaredProbeRoleValuesPass && computedProbeValuesPass && atLeastOneComputedProbeChanges
  if (!alternateReleaseProbeChangesPass) block("alternate-release-probe-unchanged")
  if (unresolvedDesignAuthorityCount > 0) block("design-authority-unresolved")
  if (unknownElementCount > 0) block("unknown-element-rendered")
  if (consoleErrorCount > 0) block("console-error-observed")

  const blockersSorted = blockers.sort()
  return {
    schemaVersion: "cogo.required-ui-block-family-live-swap-receipt.v1",
    subject: {
      kind: "required-ui-block-family",
      patternId: family.patternId,
      familyId: family.familyId,
      variantId: family.variantId,
      blockArtifactHash: family.familyBlockArtifactHash,
    },
    releases: releaseSet,
    baselineSourceAdmissionPass,
    alternateReleaseProbeChangesPass,
    ontologyReleaseSwapPass,
    blockArtifactHashUnchanged,
    structuralFingerprintUnchanged,
    interactionStatePreserved,
    focusPreservedOrContractuallyRestored,
    actionFlowTraceUnchanged,
    unresolvedDesignAuthorityCount,
    unknownElementCount,
    consoleErrorCount,
    blockers: blockersSorted,
  }
}

function releaseReceiptSet(
  releases: RequiredUiBlockFamilyEvidenceSourceRelease[],
  block: (value: string) => void,
): RequiredUiBlockFamilyLiveSwapRelease[] {
  const normalized = RELEASE_IDS.map((releaseId) =>
    releases.find((release) => release.releaseId === releaseId),
  )
  if (
    releases.length !== 3 ||
    normalized.some((release) => !release) ||
    normalized.some((release) => !/^sha256:[a-f0-9]{64}$/.test(release?.contentHash ?? ""))
  ) {
    block("release-source-set-invalid")
  }
  return normalized.map((release, index) => ({
    releaseId: RELEASE_IDS[index],
    semanticRole: release?.semanticRole ?? (
      index === 0
        ? "source-faithful-baseline"
        : index === 1
          ? "typography-radius-alternate"
          : "density-color-alternate"
    ),
    contentHash: release?.contentHash ?? "",
  }))
}

function stableNonEmpty(values: readonly string[]): boolean {
  return values.length === 3 && values[0].trim().length > 0 && new Set(values).size === 1
}

function stableSerialized(values: readonly unknown[]): boolean {
  return values.length === 3 && values.every(
    (value) => stableSerialize(value) === stableSerialize(values[0]),
  )
}

function nonNegativeInteger(value: number): number {
  return Number.isInteger(value) && value >= 0 ? value : 1
}
